// The guided-tour engine: one spotlight + one bubble, walking a tour's steps (lib/tours.ts).
//
// ⚠ ONE HOST PER SURFACE, mounted in app.tsx beside WhatsNew/FeedbackButton. Only one app branch renders at a time,
// so only one TourHost is ever live. It resumes a tour paused on THIS surface, or auto-starts the tour that begins
// here for a first-time visitor. Crossing surfaces is a real navigation — the saved step (lib/tours.ts) makes the
// next page's host pick up where this one left off.
//
// ⚠ THE SPOTLIGHT DOES NOT TRAP CLICKS. The dim is a huge box-shadow on a pointer-events:none cut-out, so the real
// control underneath stays clickable — that is what lets a step say "tap this" and mean it (advanceOnClick).
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { localDay } from "../lib/whats-new";
import {
  autoStartFor, clearNewPlayer, isTourDone, markLater, markTourDone, mayAutoStart, paragraphs, resetTour, saveStep, savedStep,
  TOURS, type Surface, type TourId, type TourStep,
} from "../lib/tours";
import { useIsPhone } from "./use-is-phone";

const START_EVENT = "compendium:start-tour";
/** The workspace listens: `{ section, tab? }` opens that rail card (right-rail.tsx ids) and expands the rail. */
export const OPEN_RAIL_EVENT = "compendium:open-rail";

/** Open a tour from anywhere — the account menu's "Show me around" row. Restarts it from the top. */
export function openTour(id: TourId): void {
  window.dispatchEvent(new CustomEvent(START_EVENT, { detail: { id } }));
}

type Active = { id: TourId; key: string };
type Rect = { top: number; left: number; width: number; height: number };

const rectOf = (el: Element): Rect => {
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height };
};
const sameRect = (a: Rect | null, b: Rect | null) =>
  a === b || (!!a && !!b && a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height);

// ⚠ `?.`: a saved id from a tutorial that was later renamed must not crash the host — it simply never resumes.
/** Does the current address already satisfy a step's `goto` — same path, and every query key it names with the same value? */
function atAddress(goto: string): boolean {
  try {
    const want = new URL(goto, window.location.origin);
    if (want.pathname.replace(/\/$/, "") !== window.location.pathname.replace(/\/$/, "")) return false;
    const have = new URLSearchParams(window.location.search);
    for (const [k, v] of want.searchParams) if (have.get(k) !== v) return false;
    return true;
  } catch { return true; }
}

const stepOf = (a: Active): TourStep | undefined => TOURS[a.id]?.steps.find((s) => s.key === a.key);
const indexOf = (a: Active): number => TOURS[a.id]?.steps.findIndex((s) => s.key === a.key) ?? -1;

export function TourHost({ surface, enabled = true, autoStart = false }: {
  surface: Surface;
  /** False keeps the host mounted but inert (a share-link TV, say). Resume and "Show me around" still need it true. */
  enabled?: boolean;
  /** Begin the tour by itself for a FIRST-TIMER on this surface. Off by default: resume and replay never need it, and an
   *  existing user must not be auto-toured. The surface decides — a GM with zero campaigns, a just-joined player. */
  autoStart?: boolean;
}) {
  const [active, setActive] = useState<Active | null>(null);
  /** This run began by itself (a first-timer): it gets a Later button as well as Skip. */
  const [autoStarted, setAutoStarted] = useState(false);
  const phone = useIsPhone();
  const [rect, setRect] = useState<Rect | null>(null);
  /** The ring slides between steps, but never INTO its first place: a transition from nowhere starts at 0,0 and, in a
   *  background tab (frozen animation timeline), stays there. True 250 ms after a ring first appears for this step. */
  const [settled, setSettled] = useState(false);
  /** An anchored step is still LOOKING for its control (≤ 3.3 s): nothing is drawn — no dim, no bubble — so a step that is
   *  about to be skipped never flashes a centred card, and one whose control mounts late never jumps from the centre. */
  const [looking, setLooking] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const bubble = useRef<HTMLDivElement>(null);
  const advancer = useRef<(() => void) | null>(null);

  const step = active ? stepOf(active) : undefined;

  // ── Advance / retreat / finish ──────────────────────────────────────────────
  const go = useCallback((a: Active | null) => { setPos(null); setRect(null); setSettled(false); setActive(a); }, []);

  const finish = useCallback(() => {
    if (active) {
      markTourDone(active.id);
      // The table tour is the last of the player's two — once it's done (or skipped), they're no longer "just joined".
      if (active.id === "table") clearNewPlayer();
    }
    go(null);
  }, [active, go]);

  /** Not now — offer once more next time (lib/tours.ts mayAutoStart). */
  const later = useCallback(() => {
    if (active) markLater(active.id);
    setAutoStarted(false);
    go(null);
  }, [active, go]);

  const next = useCallback(() => {
    if (!active) return;
    const steps = TOURS[active.id]!.steps;
    const nx = steps[indexOf(active) + 1];
    if (!nx) { finish(); return; }
    saveStep(active.id, nx.key);
    if (nx.surface !== surface) {
      // Leave this surface; the destination's host resumes. When there's no address (the table), it resumes on arrival.
      if (nx.goto) window.location.assign(nx.goto); else go(null);
    } else {
      go({ id: active.id, key: nx.key });
    }
  }, [active, surface, finish, go]);
  useEffect(() => { advancer.current = next; }, [next]);

  const back = useCallback(() => {
    if (!active) return;
    const steps = TOURS[active.id]!.steps;
    const pv = steps[indexOf(active) - 1];
    if (!pv) return;
    saveStep(active.id, pv.key);
    if (pv.surface !== surface) { if (pv.goto) window.location.assign(pv.goto); else go(null); }
    else go({ id: active.id, key: pv.key });
  }, [active, surface, go]);

  // ── Start: resume a paused tour on this surface, or (for a first-timer) auto-start the one that begins here ──────────
  useEffect(() => {
    if (!enabled) return;
    // Resume ALWAYS — a tour already under way is finished wherever the user lands, new or not.
    for (const id of Object.keys(TOURS) as TourId[]) {
      if (isTourDone(id)) continue;
      const key = savedStep(id);
      if (key && TOURS[id]!.steps.find((s) => s.key === key)?.surface === surface) {
        setActive({ id, key });
        return;
      }
    }
    // Auto-start only when the surface says this is a first-timer (a GM with no campaigns, a just-joined player) — and
    // not while a "Later" is fresh, and never after the second one.
    if (!autoStart) return;
    const auto = autoStartFor(surface, phone);
    if (auto && !isTourDone(auto) && !savedStep(auto) && mayAutoStart(auto)) {
      const first = TOURS[auto]!.steps[0]!;
      saveStep(auto, first.key);
      try { localStorage.setItem("vttmaps.whatsnew.day", localDay()); } catch { /* WhatsNew simply also opens */ }
      setAutoStarted(true);
      setActive({ id: auto, key: first.key });
    }
  }, [surface, enabled, autoStart, phone]);

  // ── "Show me around" from the account menu ──────────────────────────────────
  useEffect(() => {
    const onStart = (e: Event) => {
      const id = (e as CustomEvent<{ id: TourId }>).detail?.id;
      if (!id || !TOURS[id]) return;
      resetTour(id);
      setAutoStarted(false);
      const first = TOURS[id]!.steps[0]!;
      saveStep(id, first.key);
      if (first.surface === surface) go({ id, key: first.key });
      else if (first.goto) window.location.assign(first.goto);
    };
    window.addEventListener(START_EVENT, onStart);
    return () => window.removeEventListener(START_EVENT, onStart);
  }, [surface, go]);

  // ── Escape skips (ends this run; replayable from the menu) ───────────────────
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") finish(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, finish]);

  // ── Track the anchor element: poll (catches late mounts, scroll, layout shifts) ──────────────────
  useEffect(() => {
    if (!step) return;
    // ⚠ A `goto` on the SAME surface still has to be honoured (found live 2026-10-02): the GM's map, home and Campaign
    // settings are all "workspace", so a Campaign-settings tutorial started from the map never moved there and skipped
    // every step. If the address lacks what the step asks for, go — the saved step resumes the tour after the load.
    if (step.goto && active && !atAddress(step.goto)) { saveStep(active.id, step.key); window.location.assign(step.goto); return; }
    // Open the card and tab this step lives on, so the control is on screen before we look for it (lib/tutorials types.ts).
    if (step.open) window.dispatchEvent(new CustomEvent(OPEN_RAIL_EVENT, { detail: step.open }));
    if (!step.anchor) { setRect(null); setLooking(false); return; }
    setLooking(true);
    let tries = 0;
    let scrolled = false;
    let listened: Element | null = null;
    const onAnchorClick = () => advancer.current?.();
    const tick = () => {
      const found = document.querySelector(`[data-tour="${step.anchor}"]`);
      // ⚠ A HIDDEN ANCHOR IS A MISSING ANCHOR (feature 02, 2026-10-07): the player rail is display:none under 720 px, so its items
      // sit in the document with a zero-size box, and ringing one drew a dot in the top-left corner. No width AND no height = not
      // there: a skipIfMissing step skips, any other falls back to the centred card — never a ring around nothing.
      const el = found && (found.getBoundingClientRect().width > 0 || found.getBoundingClientRect().height > 0) ? found : null;
      if (el) {
        setLooking(false);
        if (!scrolled) { el.scrollIntoView({ block: "center", inline: "center", behavior: "smooth" }); scrolled = true; }
        setRect((prev) => { const r = rectOf(el); return sameRect(prev, r) ? prev : r; });
        if (step.advanceOnClick && listened !== el) {
          listened?.removeEventListener("click", onAnchorClick);
          el.addEventListener("click", onAnchorClick);
          listened = el;
        }
      } else {
        tries++;
        if (tries > 22) { // ~3.3s
          if (step.skipIfMissing) { advancer.current?.(); return; }
          setLooking(false);
          setRect(null); // fall back to a centred card so the tour never wedges
        }
      }
    };
    tick();
    const iv = window.setInterval(tick, 150);
    return () => { window.clearInterval(iv); listened?.removeEventListener("click", onAnchorClick); };
  }, [step]);

  // ── Place the bubble: measure, then clamp into the viewport ──────────────────
  // ⚠ Re-run when the bubble's own size settles, not just on step/rect change: a stylesheet that applies a tick
  // after mount (dev/HMR) would otherwise leave the very first bubble measured full-width and clamped to a corner.
  const place = useCallback(() => {
    if (!step) return;
    const el = bubble.current;
    if (!el) return;
    // ⚠ ON A PHONE THE BUBBLE DOCKS TO THE BOTTOM (CSS .tour-bubble.docked, above the tab bar); the spotlight ring is the
    // only pointer, because a bubble beside a control would cover it on a 375 px screen.
    if (phone) { setPos((prev) => (prev && prev.top === 0 && prev.left === 0 ? prev : { top: 0, left: 0 })); return; }
    const bw = el.offsetWidth, bh = el.offsetHeight;
    const vw = window.innerWidth, vh = window.innerHeight, gap = 12, m = 8;
    let top: number, left: number;
    if (!rect) { top = (vh - bh) / 2; left = (vw - bw) / 2; }
    else {
      const cx = rect.left + rect.width / 2, cy = rect.top + rect.height / 2;
      const p = step.placement ?? "bottom";
      if (p === "left") { left = rect.left - gap - bw; top = cy - bh / 2; }
      else if (p === "right") { left = rect.left + rect.width + gap; top = cy - bh / 2; }
      else if (p === "top") { top = rect.top - gap - bh; left = cx - bw / 2; }
      else { top = rect.top + rect.height + gap; left = cx - bw / 2; }
      if (p === "bottom" && top + bh > vh - m) top = rect.top - gap - bh; // flip up if it would fall off
      if (p === "top" && top < m) top = rect.top + rect.height + gap;     // flip down
    }
    top = Math.max(m, Math.min(top, vh - bh - m));
    left = Math.max(m, Math.min(left, vw - bw - m));
    setPos((prev) => (prev && prev.top === top && prev.left === left ? prev : { top, left }));
  }, [rect, step, phone]);
  useLayoutEffect(() => { place(); }, [place]);
  useEffect(() => {
    const el = bubble.current;
    if (!el || !step) return;
    const ro = new ResizeObserver(() => place());
    ro.observe(el);
    window.addEventListener("resize", place);
    return () => { ro.disconnect(); window.removeEventListener("resize", place); };
  }, [step, place]);

  useEffect(() => {
    if (!rect) { setSettled(false); return; }
    const t = window.setTimeout(() => setSettled(true), 250);   // a timeout, not a frame: frames never come in a hidden tab
    return () => window.clearTimeout(t);
  }, [rect === null]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (!active || !step) return null;
  if (looking && !rect) return null;
  const steps = TOURS[active.id]!.steps;
  const i = indexOf(active);
  const pad = 7;

  return (
    <div className="tour-layer" role="dialog" aria-modal="false" aria-label={step.title}>
      {rect
        ? <div className={`tour-spot${settled ? " settled" : ""}`} style={{
            top: rect.top - pad, left: rect.left - pad,
            width: rect.width + pad * 2, height: rect.height + pad * 2,
          }} />
        : <div className="tour-dim" />}
      <div className={`tour-bubble${phone ? " docked" : ""}`} ref={bubble}
           style={phone ? undefined : { top: pos?.top ?? -9999, left: pos?.left ?? -9999, visibility: pos ? "visible" : "hidden" }}>
        <h3 className="tour-title">{step.title}</h3>
        {step.clip && <img className="tour-clip" src={step.clip} alt="" loading="lazy" />}
        {paragraphs(step.body).map((p, k) => <p key={k}>{p}</p>)}
        <div className="tour-foot">
          <button className="tour-skip" onClick={finish}>Skip</button>
          {autoStarted && <button className="tour-skip" onClick={later} title="Not now — I'll offer once more next time">Later</button>}
          <span className="tour-count">{i + 1} / {steps.length}</span>
          <span className="tour-nav">
            {i > 0 && <button onClick={back}>Back</button>}
            <button className="primary" onClick={next}>{i === steps.length - 1 ? "Done" : "Next"}</button>
          </span>
        </div>
      </div>
    </div>
  );
}
